// /app/frontend/src/pages/risk/ai-assessment/constants.js
import { Bot, Brain, Database, AlertTriangle, Shield, Gauge, PenLine } from 'lucide-react';

// Step Configuration
export const STEPS = [
  { num: 1, label: 'AI System', icon: Bot },
  { num: 2, label: 'Use Case', icon: Brain },
  { num: 3, label: 'Data', icon: Database },
  { num: 4, label: 'Initial Risk', icon: AlertTriangle },
  { num: 5, label: 'Controls', icon: Shield },
  { num: 6, label: 'Residual Risk', icon: Gauge },
  { num: 7, label: 'Sign-Off', icon: PenLine },
];

export const AI_TECHNOLOGY_TYPES = [
  'Machine Learning (ML)',
  'Deep Learning',
  'Generative AI (LLM)',
  'Computer Vision',
  'Natural Language Processing',
  'Robotic Process Automation',
  'Expert/Rules-Based System',
  'Hybrid AI',
  'Other',
];

export const SECTORS = [
  'Healthcare', 'Finance & Banking', 'Insurance', 'Retail & E-commerce',
  'Manufacturing', 'Education', 'Government', 'Legal', 'HR & Recruitment',
  'Marketing', 'Transportation', 'Telecommunications', 'Energy', 'Other',
];

export const AFFECTED_POPULATIONS = [
  'Employees', 'Customers', 'Patients', 'Students', 'Citizens',
  'Job Applicants', 'Loan Applicants', 'Insurance Claimants',
  'Children', 'Elderly', 'Vulnerable Groups', 'General Public',
];

export const DATA_CATEGORIES = {
  basic: ['Name', 'Contact Details', 'Address', 'Date of Birth'],
  identifiers: ['National ID', 'Passport', 'Tax ID', 'Employee ID'],
  financial: ['Bank Details', 'Credit History', 'Income', 'Transaction Data'],
  behavioral: ['Purchase History', 'Browsing Behavior', 'App Usage', 'Preferences'],
  biometric: ['Facial Data', 'Voice Data', 'Fingerprints', 'Gait Analysis'],
  location: ['GPS Data', 'IP Address', 'Travel History', 'Movement Patterns'],
};

export const SPECIAL_CATEGORIES = [
  'Racial or Ethnic Origin', 'Political Opinions', 'Religious Beliefs',
  'Trade Union Membership', 'Genetic Data', 'Biometric Data',
  'Health Data', 'Sex Life/Orientation', 'Criminal Convictions'
];

export const TECHNICAL_CONTROLS = [
  { code: 'AI-TC-001', description: 'TEVV (Testing, Evaluation, Verification, Validation) conducted' },
  { code: 'AI-TC-002', description: 'Model performance monitoring in place' },
  { code: 'AI-TC-003', description: 'Data drift detection implemented' },
  { code: 'AI-TC-004', description: 'Adversarial attack protection' },
  { code: 'AI-TC-005', description: 'Input validation and sanitization' },
  { code: 'AI-TC-006', description: 'Model versioning and rollback capability' },
  { code: 'AI-TC-007', description: 'Audit logging for AI decisions' },
  { code: 'AI-TC-008', description: 'Explainability tools implemented' },
  { code: 'AI-TC-009', description: 'Bias testing and monitoring' },
  { code: 'AI-TC-010', description: 'Secure model deployment' },
];

export const ORGANISATIONAL_CONTROLS = [
  { code: 'AI-OP-001', description: 'AI governance policy in place' },
  { code: 'AI-OP-002', description: 'AI system registered in inventory' },
  { code: 'AI-OP-003', description: 'Model card/documentation maintained' },
  { code: 'AI-OP-004', description: 'Human oversight procedures defined' },
  { code: 'AI-OP-005', description: 'Appeal/review process for AI decisions' },
  { code: 'AI-OP-006', description: 'Staff training on AI system' },
  { code: 'AI-OP-007', description: 'Stakeholder communication plan' },
  { code: 'AI-OP-008', description: 'Regular AI system reviews scheduled' },
  { code: 'AI-OP-009', description: 'Incident response plan for AI failures' },
  { code: 'AI-OP-010', description: 'Third-party AI vendor due diligence' },
];

export const DATA_PROTECTION_CONTROLS = [
  { code: 'AI-DP-001', description: 'Privacy impact assessment conducted' },
  { code: 'AI-DP-002', description: 'Data minimization applied to training data' },
  { code: 'AI-DP-003', description: 'Consent obtained where required' },
  { code: 'AI-DP-004', description: 'Data subject rights procedures in place' },
  { code: 'AI-DP-005', description: 'Retention limits on inference data' },
  { code: 'AI-DP-006', description: 'Cross-border transfer safeguards' },
];

export const INITIAL_FORM_DATA = {
  organization_id: '',
  // Step 1
  system_name: '',
  system_description: '',
  vendor_developer: '',
  deployment_type: 'internal',
  ai_technology_type: '',
  lifecycle_stage: 'development',
  // Step 2
  purpose: '',
  intended_users: [],
  affected_populations: [],
  deployment_context: '',
  sector_domain: '',
  expected_benefits: '',
  // Step 3
  training_data_sources: '',
  personal_data_involved: false,
  data_categories: [],
  special_categories: [],
  data_subjects: [],
  data_volume: '',
  data_retention: '',
  cross_border_processing: false,
  transfer_countries: [],
  // Step 4
  initial_risk_inputs: [50, 50, 50, 50, 50, 40, 40, 30],
  initial_risk_score: null,
  initial_risk_level: null,
  initial_risk_label: null,
  initial_risk_action: null,
  initial_risk_factors: null,
  initial_trustworthiness_score: null,
  initial_data_protection_score: null,
  // Step 5
  technical_controls: [],
  organisational_controls: [],
  data_protection_controls: [],
  human_oversight_details: '',
  monitoring_approach: '',
  // Step 6
  residual_risk_inputs: [50, 50, 50, 50, 50, 40, 40, 30],
  residual_risk_score: null,
  residual_risk_level: null,
  residual_risk_label: null,
  residual_risk_action: null,
  residual_risk_factors: null,
  residual_trustworthiness_score: null,
  residual_data_protection_score: null,
  dpia_recommended: false,
  dpia_trigger_reason: null,
  linked_dpia_id: null,
  risk_acceptable: null,
  risk_acceptability_justification: '',
  // Step 7
  review_summary: '',
  nist_alignment_notes: '',
  approved_by: '',
  approval_date: '',
  approval_role: '',
  next_review_date: '',
  current_step: 1,
  status: 'draft',
};
